// Pan/zoom image viewer modelled on views/components/photoviewer.py:
// fit-to-view at zoom 0, wheel zooms in 1.25x steps under the cursor (never
// below fit), drag to pan, pinch on touch, right-click "Copy image".
const SCALE_FACTOR = 1.25;

export class PhotoViewer {
  constructor(root, { onContextMenu } = {}) {
    this.root = root;
    this.img = root.querySelector("img");
    this.coords = root.querySelector(".coords");
    this.onContextMenu = onContextMenu;
    this.zoom = 0;
    this.scale = 1;
    this.fitScale = 1;
    this.tx = 0;
    this.ty = 0;
    this.natural = { w: 0, h: 0 };
    this.pointers = new Map();
    this._bind();
    new ResizeObserver(() => this.zoom === 0 && this.resetView()).observe(root);
  }

  get hasPhoto() {
    return this.natural.w > 0;
  }

  setPhoto(url) {
    return new Promise((resolve, reject) => {
      if (!url) {
        this.img.removeAttribute("src");
        this.natural = { w: 0, h: 0 };
        resolve();
        return;
      }
      const img = this.img;
      img.onload = () => {
        this.natural = { w: img.naturalWidth, h: img.naturalHeight };
        this.zoom = 0;
        this.resetView();
        resolve();
      };
      img.onerror = () => reject(new Error("Failed to load image"));
      img.src = url;
    });
  }

  resetView() {
    if (!this.hasPhoto) return;
    const vw = this.root.clientWidth;
    const vh = this.root.clientHeight;
    if (!vw || !vh) return;
    this.fitScale = Math.min(vw / this.natural.w, vh / this.natural.h);
    this.scale = this.fitScale;
    this.tx = (vw - this.natural.w * this.scale) / 2;
    this.ty = (vh - this.natural.h * this.scale) / 2;
    this.zoom = 0;
    this._apply();
  }

  _apply() {
    this.img.style.transform = `translate(${this.tx}px, ${this.ty}px) scale(${this.scale})`;
    this.root.classList.toggle("pixelated", this.scale > 2);
  }

  _zoomAt(factor, cx, cy) {
    const next = Math.max(this.fitScale, Math.min(this.scale * factor, 64));
    if (next <= this.fitScale + 1e-9) {
      this.resetView();
      return;
    }
    const f = next / this.scale;
    this.tx = cx - (cx - this.tx) * f;
    this.ty = cy - (cy - this.ty) * f;
    this.scale = next;
    this.zoom = Math.round(Math.log(this.scale / this.fitScale) / Math.log(SCALE_FACTOR));
    this._clamp();
    this._apply();
  }

  _clamp() {
    const vw = this.root.clientWidth;
    const vh = this.root.clientHeight;
    const w = this.natural.w * this.scale;
    const h = this.natural.h * this.scale;
    this.tx = w <= vw ? (vw - w) / 2 : Math.min(0, Math.max(vw - w, this.tx));
    this.ty = h <= vh ? (vh - h) / 2 : Math.min(0, Math.max(vh - h, this.ty));
  }

  _local(e) {
    const r = this.root.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }

  _bind() {
    const root = this.root;
    root.addEventListener(
      "wheel",
      (e) => {
        if (!this.hasPhoto) return;
        e.preventDefault();
        const { x, y } = this._local(e);
        const step = Math.sign(e.deltaY);
        if (step) this._zoomAt(step < 0 ? SCALE_FACTOR : 1 / SCALE_FACTOR, x, y);
      },
      { passive: false },
    );
    root.addEventListener("dblclick", (e) => {
      if (!this.hasPhoto) return;
      if (this.zoom > 0) this.resetView();
      else {
        const { x, y } = this._local(e);
        this._zoomAt(SCALE_FACTOR ** 4, x, y);
      }
    });
    root.addEventListener("pointerdown", (e) => {
      if (!this.hasPhoto || e.button === 2) return;
      root.setPointerCapture(e.pointerId);
      this.pointers.set(e.pointerId, this._local(e));
      if (this.pointers.size === 2) {
        const [a, b] = [...this.pointers.values()];
        this.pinch = { dist: Math.hypot(a.x - b.x, a.y - b.y), scale: this.scale };
      }
      root.classList.add("dragging");
    });
    root.addEventListener("pointermove", (e) => {
      const p = this._local(e);
      if (this.hasPhoto && this.coords) {
        const ix = Math.floor((p.x - this.tx) / this.scale);
        const iy = Math.floor((p.y - this.ty) / this.scale);
        const inside = ix >= 0 && iy >= 0 && ix < this.natural.w && iy < this.natural.h;
        this.coords.textContent = inside ? `${ix}, ${iy}` : "";
      }
      const prev = this.pointers.get(e.pointerId);
      if (!prev) return;
      this.pointers.set(e.pointerId, p);
      if (this.pointers.size === 2 && this.pinch) {
        const [a, b] = [...this.pointers.values()];
        const dist = Math.hypot(a.x - b.x, a.y - b.y);
        const target = this.pinch.scale * (dist / this.pinch.dist);
        this._zoomAt(target / this.scale, (a.x + b.x) / 2, (a.y + b.y) / 2);
        return;
      }
      if (this.pointers.size === 1 && this.zoom > 0) {
        this.tx += p.x - prev.x;
        this.ty += p.y - prev.y;
        this._clamp();
        this._apply();
      }
    });
    const end = (e) => {
      this.pointers.delete(e.pointerId);
      if (this.pointers.size < 2) this.pinch = null;
      if (!this.pointers.size) root.classList.remove("dragging");
    };
    root.addEventListener("pointerup", end);
    root.addEventListener("pointercancel", end);
    root.addEventListener("pointerleave", () => this.coords && (this.coords.textContent = ""));
    root.addEventListener("contextmenu", (e) => {
      e.preventDefault();
      this.onContextMenu?.(e);
    });
  }

  /** Copy the current image to the clipboard as PNG. */
  /**
   * Copy an image (a Blob, or a promise of one) as PNG; the clipboard only takes
   * PNG images. Called right in the click handler, so a promise keeps the
   * user's gesture while the image is still being fetched.
   */
  async copyImage(source) {
    if (!this.hasPhoto || !source) return false;
    await navigator.clipboard.write([new ClipboardItem({ "image/png": Promise.resolve(source).then(toPng) })]);
    return true;
  }
}

/** The blob as PNG (re-encoded through a canvas when it is another format). */
export async function toPng(blob) {
  if (blob.type === "image/png") return blob;
  const bitmap = await createImageBitmap(blob);
  const canvas = document.createElement("canvas");
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  canvas.getContext("2d").drawImage(bitmap, 0, 0);
  bitmap.close();
  return new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("Could not convert the image"))), "image/png"));
}
